import { useEffect, useMemo, useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { useLocalization } from "@fluent/react";
import {
    Alert,
    Box,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    TextField
} from "@mui/material";
import {
    CompanyDepartmentDetails,
    createCompanyDepartment,
    DepartmentNotificationTargetEnum,
    SaveCompanyDepartment,
    updateCompanyDepartment
} from "../companyDepartmentsSlice";
import { useAppDispatch } from "../../../../app/hooks";
import { APIError } from "../../../../app/appTypes";
import { DepartmentNotificationTargetField } from "../components/DepartmentNotificationTargetField";
import { SaveButton } from "../../../../app/components/SaveButton";
import { CancelButton } from "../../../../app/components/CancelButton";

const departmentColors = [
    { value: "#1976d2", key: "company-department-color-blue" },
    { value: "#2e7d32", key: "company-department-color-green" },
    { value: "#ed6c02", key: "company-department-color-orange" },
    { value: "#d32f2f", key: "company-department-color-red" },
    { value: "#9c27b0", key: "company-department-color-purple" },
    { value: "#6d4c41", key: "company-department-color-brown" },
    { value: "#455a64", key: "company-department-color-bluegray" },
    { value: "#00838f", key: "company-department-color-turquoise" },
    { value: "#1a237e", key: "company-department-color-navy" },
    { value: "#3949ab", key: "company-department-color-indigo" },
    { value: "#ad1457", key: "company-department-color-pink" },
    { value: "#7b1fa2", key: "company-department-color-magenta" },
    { value: "#558b2f", key: "company-department-color-olive" },
    { value: "#9e6b00", key: "company-department-color-gold" },
    { value: "#8e2430", key: "company-department-color-burgundy" },
    { value: "#37474f", key: "company-department-color-charcoal" }
];

export const AddEditCompanyDepartmentDialog = ({
    department,
    mode,
    onSuccessfullySaved,
    onCancel
}: {
    department?: CompanyDepartmentDetails;
    mode: "create" | "edit";
    onSuccessfullySaved: (department: CompanyDepartmentDetails) => void;
    onCancel: () => void;
}) => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();

    const initialForm = useMemo<SaveCompanyDepartment>(() => {
        return {
            code: department?.code ?? "",
            name: department?.name ?? "",
            description: department?.description ?? "",
            isActive: department?.isActive ?? true,
            displayOrder: department?.displayOrder ?? 0,
            color: department?.color ?? departmentColors[0].value,
            parentDepartmentId: null,
            email: department?.email ?? "",
            notificationTargetType:
                department?.notificationTargetType ?? DepartmentNotificationTargetEnum.MembersOnly
        };
    }, [department]);

    const [form, setForm] = useState<SaveCompanyDepartment>(initialForm);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<APIError | undefined>(undefined);

    useEffect(() => {
        setForm(initialForm);
    }, [initialForm]);

    const handleSave = async () => {
        setIsSaving(true);
        setError(undefined);

        try {
            const dto: SaveCompanyDepartment = {
                ...form,
                code: form.code.trim().toUpperCase(),
                parentDepartmentId: null,
                displayOrder: mode === "create" ? 0 : form.displayOrder
            };

            if (mode === "create") {
                const result = await dispatch(createCompanyDepartment(dto));
                const payload = unwrapResult(result);
                onSuccessfullySaved(payload);
            } else {
                if (!department?.id) {
                    throw { kind: "unknown", message: "Missing department id" } as APIError;
                }

                const result = await dispatch(
                    updateCompanyDepartment({
                        id: department.id,
                        dto
                    })
                );

                const payload = unwrapResult(result);
                onSuccessfullySaved(payload);
            }
        } catch (err) {
            setError(err as APIError);
        } finally {
            setIsSaving(false);
        }
    };

    const isSaveDisabled =
        isSaving ||
        !form.code.trim() ||
        form.code.trim().length > 4 ||
        !form.name.trim();

    return (
        <Dialog open={true} fullWidth maxWidth="md">
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "grey.200",
                    color: "text.primary",
                    fontWeight: "bold",
                }}
            >
                {mode === "create"
                    ? l10n.getString("company-department-add-title", null, "Dodaj dział")
                    : l10n.getString("company-department-edit-title", null, "Edytuj dział")}
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                    {error && (
                        <Alert severity="error">
                            {l10n.getString("error", null, "Wystąpił błąd podczas zapisu.")}
                        </Alert>
                    )}

                    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                        <TextField
                            label={l10n.getString("company-department-code", null, "Kod")}
                            value={form.code}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    code: e.target.value
                                        .toUpperCase()
                                        .replace(/\s/g, "")
                                        .slice(0, 4)
                                }))
                            }
                            required
                            inputProps={{ maxLength: 4 }}
                            helperText={l10n.getString("company-department-code-helper", null, "_Maksymalnie 4 znaki, np. HR")}
                            sx={{ width: 140 }}
                        />

                        <TextField
                            label={l10n.getString("company-department-name", null, "Nazwa")}
                            value={form.name}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    name: e.target.value
                                }))
                            }
                            fullWidth
                            required
                        />
                    </Box>

                    <TextField
                        label={l10n.getString("company-department-description", null, "Opis")}
                        value={form.description ?? ""}
                        onChange={(e) =>
                            setForm((prev) => ({
                                ...prev,
                                description: e.target.value
                            }))
                        }
                        fullWidth
                        multiline
                        minRows={3}
                    />

                    <TextField
                        label={l10n.getString("company-department-email", null, "Email działu")}
                        value={form.email ?? ""}
                        onChange={(e) =>
                            setForm((prev) => ({
                                ...prev,
                                email: e.target.value
                            }))
                        }
                        fullWidth
                        type="email"
                    />

                    {mode === "edit" && (
                        <TextField
                            label={l10n.getString("company-department-display-order", null, "Kolejność")}
                            value={form.displayOrder}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    displayOrder: Number(e.target.value)
                                }))
                            }
                            fullWidth
                            type="number"
                        />
                    )}

                    <TextField
                        select
                        label={l10n.getString("company-department-color", null, "Kolor")}
                        value={form.color ?? ""}
                        onChange={(e) =>
                            setForm((prev) => ({
                                ...prev,
                                color: e.target.value
                            }))
                        }
                        fullWidth
                    >
                        {departmentColors.map((color) => (
                            <MenuItem key={color.value} value={color.value}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <Box
                                        sx={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: "50%",
                                            backgroundColor: color.value,
                                            border: "1px solid rgba(0,0,0,0.2)"
                                        }}
                                    />
                                    <span>
                                        {l10n.getString(color.key)}
                                    </span>
                                </Box>
                            </MenuItem>
                        ))}
                    </TextField>
                    <DepartmentNotificationTargetField
                        value={form.notificationTargetType}
                        onChange={(value) =>
                            setForm((prev) => ({
                                ...prev,
                                notificationTargetType: value
                            }))
                        }
                    />
                    {mode === "edit" && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.isActive}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            isActive: e.target.checked
                                        }))
                                    }
                                />
                            }
                            label={l10n.getString("company-department-is-active", null, "Aktywny")}
                        />
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <SaveButton
                    loading={isSaving}
                    onClick={handleSave}
                    disabled={isSaveDisabled}
                    label={mode === "create"
                        ? l10n.getString("add", null, "Dodaj")
                        : l10n.getString("save", null, "Zapisz")}
                />
                <CancelButton loading={isSaving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};